/**
 * Deployment plan oscillator strip (spec §11.3).
 *
 * Compact inline status bar rendered above the "Eligible Today" tranche list.
 * Shows the current oscillator reading + derived deployment label; clicking
 * expands a short rationale so the owner can see what the reading supports.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, Activity } from 'lucide-react';
import useOscillatorSignal from '../hooks/useOscillatorSignal';
import {
  canonicalZone,
  zoneLabel,
  zoneTokens,
  deploymentLabel,
  directionGlyph,
} from '../lib/oscillatorZone';

function fmtValue(v, digits = 1) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}`;
}

export default function DeploymentOscillatorStrip({ eligibleCount = 0 }) {
  const { status, signal } = useOscillatorSignal();
  const [expanded, setExpanded] = useState(false);

  if (status === 'loading') {
    return (
      <div className="mb-4 px-3 py-2 rounded-lg border border-slate-700/50 bg-slate-800/30 text-xs text-slate-500">
        Loading market oscillator…
      </div>
    );
  }

  if (!signal) {
    return (
      <div className="mb-4 px-3 py-2 rounded-lg border border-slate-700/50 bg-slate-800/30 text-xs text-slate-500 flex items-center gap-2">
        <Activity className="w-3.5 h-3.5" />
        Market oscillator unavailable — deployment decisions without breadth context
      </div>
    );
  }

  const zone = canonicalZone(signal.zone, signal.value);
  const zTokens = zoneTokens(zone);
  const hasEligible = eligibleCount > 0;
  const { label, tone } = deploymentLabel(signal.value, hasEligible);
  const dTokens = zoneTokens(tone);

  const rationale = (() => {
    const v = Number(signal.value);
    if (label === 'FAVORABLE') {
      return `Oscillator ${fmtValue(v)} at or below −3 with ${eligibleCount} eligible tranche${eligibleCount === 1 ? '' : 's'}. Breadth is stretched to the downside — if the thesis holds, this is the type of reading where deploying tranches historically pays best.`;
    }
    if (label === 'UNFAVORABLE') {
      return `Oscillator ${fmtValue(v)} at or above +5 with ${eligibleCount} eligible tranche${eligibleCount === 1 ? '' : 's'}. Breadth is stretched to the upside — consider deferring promotions, especially for tranches with tight ceilings.`;
    }
    if (label === 'CAUTION') {
      return `Oscillator ${fmtValue(v)} between +3 and +5. Breadth is getting extended; promote selectively and favor tranches with meaningful headroom below their ceilings.`;
    }
    return `Oscillator ${fmtValue(v)} in the neutral band (−3 to +3). No directional signal — run the deployment plan on its own criteria.`;
  })();

  return (
    <div className={`mb-4 rounded-lg border ${zTokens.borderClass} ${zTokens.bgSoft} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:brightness-110 transition-all"
      >
        <span className={`inline-block w-2 h-2 rounded-full ${zTokens.dotClass} flex-shrink-0`} />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Market
        </span>
        <span className={`font-mono font-bold ${zTokens.textClass} text-sm`}>
          {fmtValue(signal.value)}
        </span>
        <span className={`text-xs font-semibold uppercase tracking-wide ${zTokens.textClass}`}>
          {zoneLabel(zone)}
        </span>
        {signal.direction_change && (
          <span className="text-xs text-slate-500">
            {directionGlyph(signal.direction_change)}
          </span>
        )}
        <span className="hidden sm:inline text-slate-600">|</span>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Deployment
        </span>
        <span className={`text-xs font-semibold uppercase tracking-wide ${dTokens.textClass}`}>
          {label}
        </span>
        <span className="text-xs text-slate-500 hidden md:inline">
          {eligibleCount} eligible tranche{eligibleCount === 1 ? '' : 's'}
        </span>
        <span className="ml-auto text-slate-500">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 text-xs text-slate-400 leading-relaxed border-t border-slate-700/30">
          {rationale}
        </div>
      )}
    </div>
  );
}
